
/* ============================== src/components/UnreadBadge.jsx ============================== */
// src/components/UnreadBadge.jsx
import { Ic } from "./Icons";
import { ICONS } from "./iconsData";
import { messageWord } from "../utils/pluralize";

/**
 * Метка непрочитанных сообщений на карточке. Позиционируется абсолютно
 * в правом нижнем углу - родитель должен быть position: relative
 * (у .kcard и у .pj-card relative уже есть).
 *
 * Если передан onClick - бейдж становится кнопкой: клик открывает чат
 * сущности напрямую, минуя родительскую карточку. Это ключевая деталь:
 * без stopPropagation родительский onOpen перехватил бы клик и открыл
 * карточку на вкладке по умолчанию («Данные» / «Информация»), а
 * пользователь хотел именно чат.
 *
 * role="button" + tabIndex + onKeyDown - вместо вложенного <button>:
 * бейдж часто лежит внутри кликабельной карточки, которая сама может
 * быть <button> или div с onClick. Вложенные <button> - невалидный
 * HTML. Span с role даёт ту же доступность без нарушения семантики.
 *
 * variant управляет видом:
 *   - 'default' (по умолчанию) - прежний красный бейдж «4 сообщения».
 *     Сохраняет вид в TaskCard / TaskCardRow / ProjectCardRow.
 *   - 'neutral' - компактный нейтральный вид для канбана проектов:
 *     иконка чата + цифра, фон --sky-100, текст --sky-700. В плотной
 *     карточке канбана длинный текст - шум, а красный конкурирует
 *     с приоритетом (AOG / CRIT). Красный оставляем только там, где
 *     он означает проблему.
 *
 * Экспортируется и как named, и как default - потому что в разных
 * местах проекта встречаются оба стиля импорта (`import UnreadBadge`
 * и `import { UnreadBadge }`).
 */
export function UnreadBadge({ count, onClick = null, variant = "default" }) {
  if (!count) return null;

  const clickable = typeof onClick === "function";
  const neutral = variant === "neutral";

  const handleClick = clickable
    ? (e) => {
        e.stopPropagation();
        onClick();
      }
    : undefined;

  const handleKeyDown = clickable
    ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }
    : undefined;

  const className = [
    "kcard-unread",
    clickable && "kcard-unread--clickable",
    neutral && "kcard-unread--neutral",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={className}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? "Открыть чат" : undefined}
    >
      {neutral ? (
        <>
          <Ic d={ICONS.chat} size={11} />
          <span>{count}</span>
        </>
      ) : (
        <>
          {count} {messageWord(count)}
        </>
      )}
    </span>
  );
}

export default UnreadBadge;
